/**
 * Kaily OS — Studio tab: personal aesthetic & director practice,
 * reference collection, and work retrospectives. All CRUD, zero LLM.
 *
 * Backend: /api/os/practice, /api/os/refs, /api/os/retro (see
 * backend/routes/os_studio.py).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Trash2, ExternalLink, Image as ImageIcon, Check, BookMarked, Target, Clapperboard } from "lucide-react";
import { osGet, osPost, osPut, t } from "./osApi";
import { toast } from "sonner";

const SUB_TABS = [
    { id: "practice", icon: Clapperboard, en: "Practice log", cn: "练习记录" },
    { id: "refs", icon: BookMarked, en: "References", cn: "参考收藏" },
    { id: "retro", icon: Target, en: "Retrospectives", cn: "作品复盘" },
];

function Section({ title, children, action }) {
    return (
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6 mb-5">
            <div className="flex items-start justify-between gap-3 mb-4">
                <h3 className="font-serif text-xl text-[#F5F0E8]">{title}</h3>
                {action}
            </div>
            {children}
        </section>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#F5F0E8]/50 mb-1.5">{label}</div>
            {children}
        </label>
    );
}

const inputCls = "w-full rounded-lg bg-white/[0.04] border border-white/15 px-3 py-2 text-sm text-[#F5F0E8] placeholder:text-[#F5F0E8]/30 focus:border-[#D4A574] focus:outline-none";

// ────────────────────────────── 1. PRACTICE LOG ──────────────────────────────

function PracticeTab({ lang }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ title: "", kind: "aesthetic", notes: "", reference_url: "", image_url: "", tags: "" });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await osGet("/os/practice");
            setItems(data || []);
        } catch (e) { toast.error("Failed to load practice entries"); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return;
        setSaving(true);
        try {
            await osPost("/os/practice", {
                ...form,
                tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
            });
            toast.success(t(lang, "Saved", "已保存"));
            setForm({ title: "", kind: "aesthetic", notes: "", reference_url: "", image_url: "", tags: "" });
            setOpen(false);
            load();
        } catch (e) { toast.error("Save failed"); }
        finally { setSaving(false); }
    };

    const toggleDone = async (item) => {
        try {
            await osPut(`/os/practice/${item.id}`, { done: !item.done });
            setItems((prev) => prev.map((p) => p.id === item.id ? { ...p, done: !p.done } : p));
        } catch (e) { toast.error("Update failed"); }
    };
    const del = async (id) => {
        if (!window.confirm(t(lang, "Delete this entry?", "确定删除?"))) return;
        try {
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/os/practice/${id}`;
            const r = await fetch(url, { method: "DELETE", credentials: "include" });
            if (!r.ok) throw new Error("Delete failed");
            load();
        } catch (e) { toast.error("Delete failed"); }
    };

    const delSimple = async (id) => {
        if (!window.confirm(t(lang, "Delete this entry?", "确定删除?"))) return;
        try {
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/os/practice/${id}`;
            const r = await fetch(url, { method: "DELETE", credentials: "include" });
            if (!r.ok) throw new Error("Delete failed");
            load();
        } catch (e) { toast.error("Delete failed"); }
    };

    const uploadImageDirect = async (pid, file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/os/practice/${pid}/image`;
            const r = await fetch(url, { method: "POST", body: fd, credentials: "include" });
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err.detail || "Upload failed");
            }
            toast.success(t(lang, "Image uploaded", "图片已上传"));
            load();
        } catch (e) { toast.error(e.message || "Upload failed"); }
    };

    return (
        <Section
            title={t(lang, "Personal aesthetic & director practice", "个人审美与导演练习")}
            action={
                <button onClick={() => setOpen((o) => !o)} data-testid="os-practice-add"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#D4A574]/40 text-[#D4A574] text-xs px-3 py-1.5 hover:bg-[#D4A574]/10">
                    <Plus size={14} /> {t(lang, "New entry", "新增")}
                </button>
            }
        >
            {open && (
                <form onSubmit={save} className="mb-5 space-y-3" data-testid="os-practice-form">
                    <Field label={t(lang, "Title", "标题")}>
                        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="os-practice-title" placeholder={t(lang, "e.g. Study golden-hour backlight on produce", "例如:逆光研究 - 食材")} className={inputCls} required />
                    </Field>
                    <Field label={t(lang, "Kind", "类型")}>
                        <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="os-practice-kind" className={inputCls}>
                            <option value="aesthetic">{t(lang, "Aesthetic", "审美")}</option>
                            <option value="director">{t(lang, "Director practice", "导演练习")}</option>
                            <option value="both">{t(lang, "Both", "两者")}</option>
                        </select>
                    </Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label={t(lang, "Reference URL", "参考链接")}>
                            <input value={form.reference_url} onChange={(e) => setForm({ ...form, reference_url: e.target.value })} data-testid="os-practice-ref-url" placeholder="https://…" className={inputCls} />
                        </Field>
                        <Field label={t(lang, "Image URL (optional)", "图片链接(可选)")}>
                            <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="os-practice-image-url" placeholder="https://…" className={inputCls} />
                        </Field>
                    </div>
                    <Field label={t(lang, "Notes", "笔记")}>
                        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="os-practice-notes" rows={4} className={inputCls} />
                    </Field>
                    <Field label={t(lang, "Tags (comma-separated)", "标签(逗号分隔)")}>
                        <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="os-practice-tags" placeholder="food, backlight, iPhone" className={inputCls} />
                    </Field>
                    <button type="submit" disabled={saving || !form.title.trim()} data-testid="os-practice-save"
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#D4A574] text-[#1A0F0A] font-medium text-sm px-4 py-2 disabled:opacity-50">
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
                        {t(lang, "Save entry", "保存")}
                    </button>
                </form>
            )}

            {loading ? (
                <div className="py-6 flex items-center justify-center text-[#F5F0E8]/40"><Loader2 className="animate-spin" size={18} /></div>
            ) : items.length === 0 ? (
                <p className="text-[#F5F0E8]/40 text-sm" data-testid="os-practice-empty">{t(lang, "No practice entries yet.", "还没有练习记录。")}</p>
            ) : (
                <ul className="space-y-2" data-testid="os-practice-list">
                    {items.map((it, idx) => (
                        <li key={it.id} className={`rounded-xl border p-3 ${it.done ? "border-[#7BA05B]/30 bg-[#7BA05B]/5" : "border-white/10 bg-white/[0.03]"}`} data-testid={`os-practice-item-${idx}`}>
                            <div className="flex items-start gap-3">
                                {it.image_url && <img src={it.image_url} alt="" className="w-16 h-16 object-cover rounded-lg flex-shrink-0" />}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-[10px] uppercase tracking-wider text-[#D4A574]">{it.kind}</span>
                                        <h4 className={`text-sm font-medium ${it.done ? "line-through text-[#F5F0E8]/50" : "text-[#F5F0E8]"}`}>{it.title}</h4>
                                    </div>
                                    {it.notes && <p className="mt-1 text-xs text-[#F5F0E8]/70 whitespace-pre-wrap leading-relaxed">{it.notes}</p>}
                                    {it.reference_url && (
                                        <a href={it.reference_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-[11px] text-[#D4A574] hover:underline">
                                            <ExternalLink size={11} /> {it.reference_url.slice(0, 40)}…
                                        </a>
                                    )}
                                    {(it.tags || []).length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {it.tags.map((tg) => (
                                                <span key={tg} className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-[#F5F0E8]/60">#{tg}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <label className="p-1.5 rounded-full border border-white/15 text-[#F5F0E8]/40 hover:text-[#D4A574] hover:border-[#D4A574] cursor-pointer" title={t(lang, "Upload artwork", "上传作品")} data-testid={`os-practice-upload-${idx}`}>
                                        <ImageIcon size={13} />
                                        <input type="file" accept="image/*,.heic,.heif" className="hidden"
                                            onChange={(e) => { if (e.target.files?.[0]) { uploadImageDirect(it.id, e.target.files[0]); e.target.value = ""; } }} />
                                    </label>
                                    <button onClick={() => toggleDone(it)} data-testid={`os-practice-done-${idx}`}
                                        className={`p-1.5 rounded-full border ${it.done ? "border-[#7BA05B] text-[#7BA05B]" : "border-white/15 text-[#F5F0E8]/40 hover:text-[#7BA05B]"}`}>
                                        <Check size={13} />
                                    </button>
                                    <button onClick={() => delSimple(it.id)} data-testid={`os-practice-del-${idx}`}
                                        className="p-1.5 rounded-full border border-white/15 text-[#F5F0E8]/40 hover:text-[#C0604A] hover:border-[#C0604A]">
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </Section>
    );
}

// ────────────────────────────── 2. REFERENCES ──────────────────────────────

function RefsTab({ lang }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ title: "", url: "", image_url: "", source: "", tags: "", note: "" });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await osGet("/os/refs");
            setItems(data || []);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return;
        setSaving(true);
        try {
            await osPost("/os/refs", { ...form, tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean) });
            toast.success(t(lang, "Reference saved", "参考已保存"));
            setForm({ title: "", url: "", image_url: "", source: "", tags: "", note: "" });
            setOpen(false);
            load();
        } catch (e) { toast.error("Save failed"); }
        finally { setSaving(false); }
    };
    const del = async (id) => {
        if (!window.confirm(t(lang, "Delete this reference?", "确定删除?"))) return;
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/os/refs/${id}`, { method: "DELETE", credentials: "include" });
        if (r.ok) load();
    };

    return (
        <Section
            title={t(lang, "Reference collection", "参考收藏")}
            action={
                <button onClick={() => setOpen((o) => !o)} data-testid="os-refs-add"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#D4A574]/40 text-[#D4A574] text-xs px-3 py-1.5 hover:bg-[#D4A574]/10">
                    <Plus size={14} /> {t(lang, "Save reference", "新增参考")}
                </button>
            }
        >
            {open && (
                <form onSubmit={save} className="mb-5 space-y-3" data-testid="os-refs-form">
                    <Field label={t(lang, "Title", "标题")}>
                        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="os-refs-title" required className={inputCls} />
                    </Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label={t(lang, "URL", "链接")}>
                            <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="os-refs-url" placeholder="https://…" className={inputCls} />
                        </Field>
                        <Field label={t(lang, "Image URL", "图片链接")}>
                            <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="os-refs-image" placeholder="https://…" className={inputCls} />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label={t(lang, "Source", "来源")}>
                            <input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="os-refs-source" placeholder="Vimeo / IG / Shelf book…" className={inputCls} />
                        </Field>
                        <Field label={t(lang, "Tags", "标签")}>
                            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="os-refs-tags" placeholder="food, moody, backlight" className={inputCls} />
                        </Field>
                    </div>
                    <Field label={t(lang, "Note", "笔记")}>
                        <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="os-refs-note" rows={3} className={inputCls} />
                    </Field>
                    <button type="submit" disabled={saving || !form.title.trim()} data-testid="os-refs-save"
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#D4A574] text-[#1A0F0A] font-medium text-sm px-4 py-2 disabled:opacity-50">
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
                        {t(lang, "Save", "保存")}
                    </button>
                </form>
            )}

            {loading ? (
                <div className="py-6 flex items-center justify-center text-[#F5F0E8]/40"><Loader2 className="animate-spin" size={18} /></div>
            ) : items.length === 0 ? (
                <p className="text-[#F5F0E8]/40 text-sm" data-testid="os-refs-empty">{t(lang, "No references saved yet.", "还没有参考资料。")}</p>
            ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="os-refs-list">
                    {items.map((it, idx) => (
                        <li key={it.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`os-refs-item-${idx}`}>
                            <div className="flex gap-3">
                                {it.image_url ? (
                                    <img src={it.image_url} alt="" className="w-20 h-20 object-cover rounded-lg flex-shrink-0" />
                                ) : (
                                    <div className="w-20 h-20 rounded-lg flex-shrink-0 bg-white/5 flex items-center justify-center text-[#F5F0E8]/20"><ImageIcon size={18} /></div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium text-[#F5F0E8]">{it.title}</h4>
                                    {it.source && <p className="text-[11px] text-[#F5F0E8]/50">{it.source}</p>}
                                    {it.note && <p className="text-xs text-[#F5F0E8]/70 mt-1 line-clamp-2">{it.note}</p>}
                                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                                        {it.url && <a href={it.url} target="_blank" rel="noreferrer" className="text-[11px] text-[#D4A574] inline-flex items-center gap-1"><ExternalLink size={10} />{t(lang, "Open", "打开")}</a>}
                                        {(it.tags || []).map((tg) => <span key={tg} className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-[#F5F0E8]/60">#{tg}</span>)}
                                    </div>
                                </div>
                                <button onClick={() => del(it.id)} data-testid={`os-refs-del-${idx}`}
                                    className="p-1.5 rounded-full border border-white/15 text-[#F5F0E8]/40 hover:text-[#C0604A] hover:border-[#C0604A] flex-shrink-0 h-fit">
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </Section>
    );
}

// ────────────────────────────── 3. RETROSPECTIVES ──────────────────────────────

function RetrosTab({ lang }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({ project_slug: "", title: "", date: today, what_worked: "", what_didnt: "", next_time: "", rating: "" });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await osGet("/os/retro");
            setItems(data || []);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return;
        setSaving(true);
        try {
            await osPost("/os/retro", { ...form, rating: form.rating ? Number(form.rating) : null });
            toast.success(t(lang, "Retrospective saved", "复盘已保存"));
            setForm({ project_slug: "", title: "", date: today, what_worked: "", what_didnt: "", next_time: "", rating: "" });
            setOpen(false);
            load();
        } catch (e) { toast.error("Save failed"); }
        finally { setSaving(false); }
    };
    const del = async (id) => {
        if (!window.confirm(t(lang, "Delete this retrospective?", "确定删除?"))) return;
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/os/retro/${id}`, { method: "DELETE", credentials: "include" });
        if (r.ok) load();
    };

    return (
        <Section
            title={t(lang, "Work retrospectives", "作品复盘")}
            action={
                <button onClick={() => setOpen((o) => !o)} data-testid="os-retro-add"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#D4A574]/40 text-[#D4A574] text-xs px-3 py-1.5 hover:bg-[#D4A574]/10">
                    <Plus size={14} /> {t(lang, "New retrospective", "新增复盘")}
                </button>
            }
        >
            {open && (
                <form onSubmit={save} className="mb-5 space-y-3" data-testid="os-retro-form">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Field label={t(lang, "Title", "标题")}>
                            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="os-retro-title" required className={inputCls} />
                        </Field>
                        <Field label={t(lang, "Project slug (optional)", "作品 slug(可选)")}>
                            <input value={form.project_slug} onChange={(e) => setForm({ ...form, project_slug: e.target.value })} data-testid="os-retro-slug" className={inputCls} />
                        </Field>
                        <Field label={t(lang, "Date", "日期")}>
                            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="os-retro-date" className={inputCls} />
                        </Field>
                    </div>
                    <Field label={t(lang, "What worked", "做得好的")}>
                        <textarea value={form.what_worked} onChange={(e) => setForm({ ...form, what_worked: e.target.value })} data-testid="os-retro-worked" rows={3} className={inputCls} />
                    </Field>
                    <Field label={t(lang, "What didn't", "做得不够的")}>
                        <textarea value={form.what_didnt} onChange={(e) => setForm({ ...form, what_didnt: e.target.value })} data-testid="os-retro-didnt" rows={3} className={inputCls} />
                    </Field>
                    <Field label={t(lang, "Next time", "下次改进")}>
                        <textarea value={form.next_time} onChange={(e) => setForm({ ...form, next_time: e.target.value })} data-testid="os-retro-next" rows={3} className={inputCls} />
                    </Field>
                    <Field label={t(lang, "Self rating (1-10)", "自评(1-10)")}>
                        <input type="number" min={1} max={10} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="os-retro-rating" className={inputCls} />
                    </Field>
                    <button type="submit" disabled={saving || !form.title.trim()} data-testid="os-retro-save"
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#D4A574] text-[#1A0F0A] font-medium text-sm px-4 py-2 disabled:opacity-50">
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
                        {t(lang, "Save retrospective", "保存复盘")}
                    </button>
                </form>
            )}

            {loading ? (
                <div className="py-6 flex items-center justify-center text-[#F5F0E8]/40"><Loader2 className="animate-spin" size={18} /></div>
            ) : items.length === 0 ? (
                <p className="text-[#F5F0E8]/40 text-sm" data-testid="os-retro-empty">{t(lang, "No retrospectives yet.", "还没有复盘。")}</p>
            ) : (
                <ul className="space-y-3" data-testid="os-retro-list">
                    {items.map((it, idx) => (
                        <li key={it.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={`os-retro-item-${idx}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <h4 className="text-sm font-medium text-[#F5F0E8]">{it.title}</h4>
                                        <span className="text-[11px] text-[#F5F0E8]/50">{it.date}</span>
                                        {it.rating && <span className="text-[11px] text-[#D4A574] bg-[#D4A574]/10 px-2 py-0.5 rounded-full">{it.rating}/10</span>}
                                        {it.project_slug && <span className="text-[11px] text-[#7BA05B] font-mono">#{it.project_slug}</span>}
                                    </div>
                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs leading-relaxed">
                                        {it.what_worked && <div><div className="text-[10px] uppercase tracking-wider text-[#7BA05B] mb-1">{t(lang, "Worked", "做得好")}</div><p className="text-[#F5F0E8]/80 whitespace-pre-wrap">{it.what_worked}</p></div>}
                                        {it.what_didnt && <div><div className="text-[10px] uppercase tracking-wider text-[#C0604A] mb-1">{t(lang, "Didn't", "不足")}</div><p className="text-[#F5F0E8]/80 whitespace-pre-wrap">{it.what_didnt}</p></div>}
                                        {it.next_time && <div><div className="text-[10px] uppercase tracking-wider text-[#D4A574] mb-1">{t(lang, "Next time", "下次")}</div><p className="text-[#F5F0E8]/80 whitespace-pre-wrap">{it.next_time}</p></div>}
                                    </div>
                                </div>
                                <button onClick={() => del(it.id)} data-testid={`os-retro-del-${idx}`}
                                    className="p-1.5 rounded-full border border-white/15 text-[#F5F0E8]/40 hover:text-[#C0604A] hover:border-[#C0604A] flex-shrink-0">
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </Section>
    );
}

// ────────────────────────────── Combined panel ──────────────────────────────

export default function OSStudio({ lang, only }) {
    // `only` restricts to one sub-tab for Phase 6 OS 5-nav split
    const SUBS = only ? SUB_TABS.filter((s) => s.id === only) : SUB_TABS;
    const [sub, setSub] = useState(only || "practice");
    return (
        <div data-testid="os-studio-panel">
            {!only && (
                <div className="flex gap-1 mb-5 overflow-x-auto">
                    {SUBS.map((tb) => (
                        <button key={tb.id} onClick={() => setSub(tb.id)} data-testid={`os-studio-tab-${tb.id}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-[0.14em] font-semibold border-b-2 transition-colors whitespace-nowrap ${sub === tb.id ? "border-[#D4A574] text-[#D4A574]" : "border-transparent text-[#F5F0E8]/45 hover:text-[#F5F0E8]"}`}>
                            <tb.icon size={13} /> {t(lang, tb.en, tb.cn)}
                        </button>
                    ))}
                </div>
            )}
            {sub === "practice" && <PracticeTab lang={lang} />}
            {sub === "refs" && <RefsTab lang={lang} />}
            {sub === "retro" && <RetrosTab lang={lang} />}
        </div>
    );
}
